import { APP_VERSION } from '@/config/constants';
import { FiCopy } from 'react-icons/fi';
import { Fragment } from 'react';
import { SoftButton } from 'components/Button';
import { fetchAdminData } from 'utils/api/admin.api';
import styled from 'styled-components';
import toast from 'react-hot-toast';
import { useDataSourceVersion } from 'utils/hooks';
import { useQuery } from 'react-query';

const CountryMetadata = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
`;

const CountryMetadatum = styled.div`
    background-color: ${({ theme }) => theme.gray_5};
    border-radius: ${({ theme }) => theme.borderRadius_md};
    padding: 1rem;
    margin: 0;
    flex-grow: 1;
    flex-shrink: 0;
    min-width: 12.5rem;
`;

const CountryMetadatumError = styled(CountryMetadatum)`
    background-color: ${({ theme }) => theme.red_100};
    color: ${({ theme }) => theme.red_800};
`;

const CountryMetadatumList = styled.dl`
    display: grid;
    grid-column-gap: 1rem;
    grid-template-columns: max-content 1fr;
`;

const Term = styled.dl`
    margin: 0;
    font-weight: bold;
    text-align: right;
    text-transform: capitalize;
`;

const Definition = styled.dd`
    margin: 0;
    text-transform: uppercase;
`;

async function copyToClipboard(text) {
    await navigator.clipboard.writeText(text);
    toast.success('Copied!');
}

function DataSourceMetadata({ dataSource, countries, dbName }) {
    return (
        <p>
            <details>
                <summary>
                    <strong
                        css={`
                            text-transform: capitalize;
                        `}
                    >
                        {dataSource}
                    </strong>
                    <span> - </span>
                    <span
                        css={`
                            display: inline-flex;
                            align-items: center;
                        `}
                    >
                        {dbName}
                        <SoftButton
                            onClick={() => copyToClipboard(dbName)}
                            css={`
                                margin-left: 0.5rem;
                            `}
                            aria-label={`Copy ${dbName} to clipboard`}
                            IconLeft={FiCopy}
                        />
                    </span>
                </summary>
                <CountryMetadata>
                    {Object.entries(countries || {}).map(
                        ([countryTextId, countryMetadata]) => (
                            <CountryMetadatum key={countryTextId}>
                                <strong>{countryTextId}</strong>
                                <CountryMetadatumList>
                                    {Object.entries(countryMetadata).map(
                                        ([key, value]) => (
                                            <Fragment key={key}>
                                                <Term>
                                                    {key.replace('_', ' ')}
                                                </Term>
                                                <Definition>{value}</Definition>
                                            </Fragment>
                                        ),
                                    )}
                                </CountryMetadatumList>
                            </CountryMetadatum>
                        ),
                    )}
                </CountryMetadata>
            </details>
        </p>
    );
}

function DataSourceMetadataLoading({ dataSource }) {
    return (
        <p>
            <details>
                <summary>
                    <strong
                        css={`
                            text-transform: capitalize;
                        `}
                    >
                        {dataSource}
                    </strong>
                </summary>
                <CountryMetadata>
                    <CountryMetadatum>
                        <strong>Loading...</strong>
                    </CountryMetadatum>
                </CountryMetadata>
            </details>
        </p>
    );
}

function DataSourceMetadataError({ dataSource, error }) {
    return (
        <p>
            <details>
                <summary>
                    <strong
                        css={`
                            text-transform: capitalize;
                        `}
                    >
                        {dataSource}
                    </strong>
                </summary>
                <CountryMetadata>
                    <CountryMetadatumError>
                        <strong>Error: {error.message}</strong>
                    </CountryMetadatumError>
                </CountryMetadata>
            </details>
        </p>
    );
}

function Info() {
    const { data } = useQuery('admin', fetchAdminData, {
        cacheTime: 0,
        staleTime: 0,
    });
    const dataSourceVersions = useDataSourceVersion();

    const { last_updated, environment_name, redis } = data || {};

    return (
        <article>
            <h3>Info</h3>
            {Object.entries(dataSourceVersions).map(
                ([dataSourceVersion, dataSourceQuery]) => {
                    const dataSource = dataSourceVersion
                        .replace('Version', '')
                        .toLowerCase();
                    if (dataSourceQuery.isLoading) {
                        return (
                            <DataSourceMetadataLoading
                                key={dataSource}
                                dataSource={dataSource}
                            />
                        );
                    }
                    if (dataSourceQuery.isError) {
                        return (
                            <DataSourceMetadataError
                                key={dataSource}
                                dataSource={dataSource}
                                error={dataSourceQuery.error}
                            />
                        );
                    }
                    const { data } = dataSourceQuery;
                    return (
                        <DataSourceMetadata
                            key={dataSource}
                            dataSource={dataSource}
                            countries={data?.countries}
                            dbName={data?.dbName}
                        />
                    );
                },
            )}
            <p>
                <strong>Last Updated: </strong>
                {last_updated}
            </p>
            <p>
                <strong> App Version: </strong> {APP_VERSION}
            </p>
            <p>
                <strong> Environment: </strong> {environment_name}
            </p>
            <p>
                <strong> Redis: </strong> {redis}
            </p>
        </article>
    );
}

export default Info;
